import type { ApiClient } from "../api-client";
import type {
  CreateTripInput,
  CreateTripResponse,
  SetTripCampInput,
  SetTripCampResponse,
  SetTripRouteResponse,
  TripDetailResponse,
  TripShareUrlResponse,
  TripSuggestionsResponse,
  TripsResponse,
  UpdateTripInput,
} from "../types/trips";

// Trips (spec 35 / ATL-157). Endpoint paths mirror packages/api/src/routes/trips.ts.

export function fetchTrips(client: ApiClient): Promise<TripsResponse> {
  return client.get("/trips");
}

export function createTrip(client: ApiClient, input: CreateTripInput): Promise<CreateTripResponse> {
  return client.post("/trips", input);
}

export function fetchTrip(client: ApiClient, id: string): Promise<TripDetailResponse> {
  return client.get(`/trips/${id}`);
}

export function updateTrip(client: ApiClient, id: string, input: UpdateTripInput): Promise<{ ok: true }> {
  return client.patch(`/trips/${id}`, input);
}

/** Ungroups the trip. The activities are untouched. */
export function deleteTrip(client: ApiClient, id: string): Promise<{ ok: true }> {
  return client.delete(`/trips/${id}`);
}

/** `all` = the whole history (on demand, up to 20) instead of the recent window. */
export function fetchTripSuggestions(client: ApiClient, opts?: { all?: boolean }): Promise<TripSuggestionsResponse> {
  return client.get(`/trips/suggestions${opts?.all ? "?all=1" : ""}`);
}

/** Spec 38: use one of the user's activities as the trip's planned route. Fills are computed before the answer. */
export function setTripRoute(client: ApiClient, id: string, activityId: string): Promise<SetTripRouteResponse> {
  return client.put(`/trips/${id}/route`, { activityId });
}

/** Spec 38: drop the planned route (fills go; trail credit already earned stays). */
export function deleteTripRoute(client: ApiClient, id: string): Promise<{ ok: true }> {
  return client.delete(`/trips/${id}/route`);
}

/** Round 2: move the camp of the night after `fromActivityId` (the server snaps it to the route or a recording). */
export function setTripCamp(
  client: ApiClient,
  id: string,
  fromActivityId: string,
  input: SetTripCampInput,
): Promise<SetTripCampResponse> {
  return client.put(`/trips/${id}/camps/${fromActivityId}`, input);
}

/** Round 2: put a moved camp back to the automatic one (GPX waypoint, else the end of that evening's recording). */
export function resetTripCamp(client: ApiClient, id: string, fromActivityId: string): Promise<{ ok: true }> {
  return client.delete(`/trips/${id}/camps/${fromActivityId}`);
}

/** Spec 38: multipart upload path (field `file`, a .gpx) for a route that never becomes an activity. */
export function tripRouteUploadPath(id: string): string {
  return `/trips/${id}/route/upload`;
}

export function dismissTripSuggestion(client: ApiClient, firstActivityId: string): Promise<{ ok: true }> {
  return client.post("/trips/suggestions/dismiss", { firstActivityId });
}

export function fetchTripShareUrl(client: ApiClient, id: string, identity: boolean): Promise<TripShareUrlResponse> {
  return client.get(`/trips/${id}/share-url${identity ? "?identity=1" : ""}`);
}

/** Owner-only GPX (one trk per member). Authenticated: download with the auth header. */
export function tripGpxPath(id: string): string {
  return `/trips/${id}/gpx`;
}
